import { useState } from "react";
import { motion } from "framer-motion";
import SceneShell from "../components/SceneShell";
import { AnimatedLogo } from "../components/ProductLogo";
import IPhoneFrame from "../components/IPhoneFrame";
import VideoLightbox from "../components/VideoLightbox";

/**
 * Scene 01 — Opening Hold (replaces Video Intro)
 *
 * A living, breathing holding screen the speaker delivers their opening
 * speech over. Three product logos drift in/out; bokeh particles float
 * across the stage. SOCIAL WEEK is permanently blurred — the "one more
 * thing" tease.
 */

// Bokeh particle: a soft blurred circle that drifts on a long loop.
function Bokeh({ color, size, x, y, dx, dy, dur, delay = 0, opacity = 0.55 }) {
  return (
    <motion.span
      className="absolute rounded-full pointer-events-none"
      style={{
        width: size,
        height: size,
        left: `${x}%`,
        top: `${y}%`,
        background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        filter: "blur(28px)",
        opacity,
      }}
      animate={{
        x: [0, dx, 0, -dx * 0.5, 0],
        y: [0, dy, dy * 0.4, -dy * 0.3, 0],
      }}
      transition={{
        duration: dur,
        repeat: Infinity,
        ease: "easeInOut",
        delay,
      }}
    />
  );
}

export default function Scene01_OpeningHold() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  return (
    <SceneShell
      variant="ink"
      align="center"
      data-testid="scene-opening-hold"
      contentClassName="overflow-hidden"
    >
      {/* Slow pulsing ambient glow under the stage */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at 28% 60%, rgba(150,193,31,0.18) 0%, transparent 55%), radial-gradient(ellipse at 72% 40%, rgba(0,222,182,0.12) 0%, transparent 55%)",
            "radial-gradient(ellipse at 72% 60%, rgba(150,193,31,0.14) 0%, transparent 55%), radial-gradient(ellipse at 28% 40%, rgba(212,148,10,0.16) 0%, transparent 55%)",
            "radial-gradient(ellipse at 28% 60%, rgba(150,193,31,0.18) 0%, transparent 55%), radial-gradient(ellipse at 72% 40%, rgba(0,222,182,0.12) 0%, transparent 55%)",
          ],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Bokeh field — 12 drifting blurred circles */}
      <div className="absolute inset-0 pointer-events-none" data-testid="bokeh-field">
        <Bokeh color="rgba(150,193,31,0.55)" size={180} x={8} y={20} dx={120} dy={80} dur={22} />
        <Bokeh color="rgba(0,222,182,0.50)" size={150} x={88} y={12} dx={-100} dy={70} dur={26} delay={2} />
        <Bokeh color="rgba(212,148,10,0.40)" size={210} x={70} y={75} dx={-90} dy={-60} dur={28} delay={4} />
        <Bokeh color="rgba(150,193,31,0.35)" size={120} x={45} y={85} dx={70} dy={-50} dur={20} delay={1} />
        <Bokeh color="rgba(0,222,182,0.30)" size={100} x={20} y={75} dx={60} dy={-40} dur={18} delay={3} />
        <Bokeh color="rgba(212,148,10,0.30)" size={140} x={92} y={50} dx={-70} dy={50} dur={24} delay={5} opacity={0.4} />
        <Bokeh color="rgba(150,193,31,0.25)" size={80} x={60} y={15} dx={-50} dy={50} dur={16} delay={2.5} opacity={0.45} />
        <Bokeh color="rgba(255,255,255,0.10)" size={220} x={50} y={50} dx={40} dy={30} dur={30} delay={6} opacity={0.6} />
        <Bokeh color="rgba(0,222,182,0.30)" size={90} x={5} y={55} dx={50} dy={-40} dur={19} delay={4} opacity={0.5} />
        <Bokeh color="rgba(150,193,31,0.40)" size={110} x={35} y={35} dx={40} dy={60} dur={21} delay={3.5} opacity={0.4} />
        <Bokeh color="rgba(212,148,10,0.30)" size={130} x={15} y={90} dx={-50} dy={-30} dur={23} delay={1.2} opacity={0.4} />
        <Bokeh color="rgba(0,222,182,0.30)" size={170} x={80} y={88} dx={-60} dy={-40} dur={25} delay={5.5} opacity={0.35} />
      </div>

      {/* Three product logos — moved to corners so the iPhone can sit centred */}
      <div className="absolute inset-0 pointer-events-none" data-testid="logo-stage">
        <AnimatedLogo
          drift="go"
          anchor={{ left: "12%", top: "30%", transform: "translate(-50%, -50%)" }}
          logoProps={{ name: "GO", color: "#96C11F", testId: "social-go" }}
        />
        <AnimatedLogo
          drift="pro"
          anchor={{ left: "88%", top: "30%", transform: "translate(-50%, -50%)" }}
          logoProps={{ name: "PRO", color: "#e2e8f0", testId: "social-pro" }}
        />
        <AnimatedLogo
          drift="week"
          anchor={{ left: "50%", top: "92%", transform: "translate(-50%, -50%)" }}
          logoProps={{
            name: "WEEK",
            color: "#e9a820",
            gradient: "linear-gradient(135deg, #d4940a 0%, #e9a820 50%, #d4940a 100%)",
            blur: 9,
            testId: "social-week",
          }}
        />
      </div>

      {/* iPhone with intro video — centred on stage */}
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <IPhoneFrame
          width={340}
          onPlay={() => setLightboxOpen(true)}
          posterUrl="/video-posters/opening-iphone.png"
        />
      </div>

      <VideoLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        streamableId="c0f05z"
        label="Opening · intro video"
      />
    </SceneShell>
  );
}
